// ============================================================================
// BOQ — formatted bill HTML for print / save-as-PDF
// ============================================================================
// Builds a self-contained, print-ready HTML document for a project's priced
// bill: BRIO header, lines grouped by trade with subtotals, a grand total,
// repeating column headers across pages, and A4 print CSS. Opened in a new
// window where the user prints or saves as PDF (works on desktop and iPad).
// ============================================================================

import { formatMoney, formatQty } from './money';
import { effectiveUnit } from './measureUnits';

const esc = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const qty = (v) => formatQty(v);

const CLAUSE_HEADINGS = {
  inclusion: 'Inclusions',
  exclusion: 'Exclusions',
  assumption: 'Assumptions',
  qualification: 'Qualifications',
};

// Render the inclusions/exclusions/assumptions/qualifications block.
function clausesHtml(clauses) {
  if (!clauses || clauses.length === 0) return '';
  const order = ['inclusion', 'exclusion', 'assumption', 'qualification'];
  const blocks = order.map((kind) => {
    const list = clauses.filter((c) => c.kind === kind);
    if (list.length === 0) return '';
    const lis = list
      .slice()
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
      .map((c) => `<li>${esc(c.text)}</li>`).join('');
    return `<div class="clause-block"><h3>${esc(CLAUSE_HEADINGS[kind])}</h3><ul>${lis}</ul></div>`;
  }).join('');
  return `<div class="clauses">${blocks}</div>`;
}

export function renderBillHtml(items, { projectName, projectCode, currency = 'INR', basis = 'quoted', clauses = [] }) {
  const rateKey = basis === 'billed' ? 'rateBilled' : 'rateQuoted';
  const amtKey = basis === 'billed' ? 'amountBilled' : 'amountQuoted';

  // Split selected vs unselected alternates: only selected lines feed the
  // priced bill; unselected alternates print at the end as an appendix.
  const billItems = items.filter((it) => it.isSelected !== false);
  const alternateItems = items.filter((it) => it.optionGroup && it.isSelected === false);

  // Group measured lines by trade (first 2 letters of the item code); SD
  // allowance lines by their sub-trade/trade; true lump sums into one bucket.
  const isTrueLump = (lt) => lt === 'provisional_sum' || lt === 'pc_sum' || lt === 'dayworks';
  const groups = new Map();
  billItems.forEach((it) => {
    const code = it.version?.fullCode || '';
    let trade;
    if (isTrueLump(it.lineType)) trade = 'Provisional sums & PC items';
    else if (it.lineType === 'allowance') trade = it.subtrade || it.trade || 'Other';
    else trade = code.slice(0, 2) || 'Other';
    if (!groups.has(trade)) groups.set(trade, []);
    groups.get(trade).push(it);
  });
  // Measured trades first (alpha), provisional/PC group last.
  const PROV = 'Provisional sums & PC items';
  const sortedTrades = [...groups.keys()].filter((t) => t !== PROV).sort();
  if (groups.has(PROV)) sortedTrades.push(PROV);

  let grand = 0;
  const sections = sortedTrades.map((trade) => {
    const lines = groups.get(trade);
    let subtotal = 0;
    const rows = lines.map((it) => {
      const amount = it[amtKey];
      if (amount != null) subtotal += Number(amount);
      const lumpTag = { provisional_sum: 'PROV', pc_sum: 'PC', dayworks: 'DAYW' }[it.lineType] || '';
      return `<tr>
        <td class="code">${esc(it.version?.fullCode || lumpTag)}</td>
        <td class="desc">${esc(it.descriptionOverride || it.version?.shortDescription || '')}</td>
        <td class="unit">${esc(effectiveUnit(it))}</td>
        <td class="num">${qty(it.quantity)}</td>
        <td class="num">${it[rateKey] == null ? '' : esc(formatMoney(it[rateKey], currency))}</td>
        <td class="num">${amount == null ? '' : esc(formatMoney(amount, currency))}</td>
      </tr>`;
    }).join('');
    grand += subtotal;
    return `
      <tr class="group"><td colspan="6">${esc(trade)}</td></tr>
      ${rows}
      <tr class="subtotal"><td colspan="5">${esc(trade)} subtotal</td><td class="num">${esc(formatMoney(subtotal, currency))}</td></tr>
    `;
  }).join('');

  // Alternates appendix: group by option_group label so each decision shows
  // its candidates with their (excluded) amounts. Selected alternate is
  // tagged so the cover trail makes the choice clear.
  const altGroups = new Map();
  alternateItems.forEach((it) => {
    const key = it.optionGroup || 'Other alternates';
    if (!altGroups.has(key)) altGroups.set(key, []);
    altGroups.get(key).push(it);
  });
  // Also surface the corresponding *selected* alternate (same option_group) so
  // the reader can see what was chosen vs what was put aside.
  const selectedByGroup = new Map();
  billItems.forEach((it) => { if (it.optionGroup) selectedByGroup.set(it.optionGroup, it); });

  const altSectionsHtml = altGroups.size === 0 ? '' : [...altGroups.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([group, lines]) => {
      const picked = selectedByGroup.get(group);
      const pickedRow = picked ? `
        <tr class="alt-picked">
          <td class="code">${esc(picked.version?.fullCode || '')}</td>
          <td class="desc"><strong>Selected:</strong> ${esc(picked.descriptionOverride || picked.version?.shortDescription || '')}</td>
          <td class="unit">${esc(effectiveUnit(picked))}</td>
          <td class="num">${qty(picked.quantity)}</td>
          <td class="num">${picked[rateKey] == null ? '' : esc(formatMoney(picked[rateKey], currency))}</td>
          <td class="num">${picked[amtKey] == null ? '' : esc(formatMoney(picked[amtKey], currency))}</td>
        </tr>` : '';
      const altRows = lines.map((it) => `
        <tr class="alt">
          <td class="code">${esc(it.version?.fullCode || '')}</td>
          <td class="desc">${esc(it.descriptionOverride || it.version?.shortDescription || '')}</td>
          <td class="unit">${esc(effectiveUnit(it))}</td>
          <td class="num">${qty(it.quantity)}</td>
          <td class="num">${it[rateKey] == null ? '' : esc(formatMoney(it[rateKey], currency))}</td>
          <td class="num">${it[amtKey] == null ? '' : esc(formatMoney(it[amtKey], currency))}</td>
        </tr>`).join('');
      return `
        <tr class="group"><td colspan="6">Option: ${esc(group)}</td></tr>
        ${pickedRow}
        ${altRows}
      `;
    }).join('');

  const today = new Date().toISOString().slice(0, 10);
  const basisLabel = basis === 'billed' ? 'Tender' : 'Estimate';

  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8" />
<title>BOQ ${esc(projectCode || '')}</title>
<style>
  @page { size: A4; margin: 16mm 12mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Raleway', -apple-system, system-ui, sans-serif; color: #222; margin: 0; }
  .no-print { margin: 12px; }
  .no-print button { font: inherit; padding: 8px 16px; border: 1px solid #9F902A; background: #9F902A; color: #fff; border-radius: 4px; cursor: pointer; }
  .doc { padding: 0 4px; }
  .head { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #9F902A; padding-bottom: 8px; margin-bottom: 14px; }
  .head .brand { font-weight: 700; font-size: 18px; letter-spacing: 0.5px; color: #9F902A; }
  .head h1 { font-size: 16px; margin: 4px 0 0; }
  .head .meta { text-align: right; font-size: 11px; color: #555; line-height: 1.6; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  thead { display: table-header-group; }
  thead th { background: #9F902A; color: #fff; text-align: left; padding: 5px 6px; font-weight: 600; }
  thead th.num { text-align: right; }
  tbody td { padding: 4px 6px; border-bottom: 1px solid #eee; vertical-align: top; }
  td.num { text-align: right; white-space: nowrap; }
  td.code { font-family: 'Courier New', monospace; white-space: nowrap; }
  td.unit { white-space: nowrap; }
  tr.group td { background: #f5f0d4; font-weight: 700; padding-top: 8px; }
  tr.subtotal td { font-weight: 600; border-top: 1px solid #ccc; border-bottom: 2px solid #9F902A; }
  tr.alt td { color: #777; font-style: italic; }
  tr.alt-picked td { background: #fbf6dd; }
  tr { page-break-inside: avoid; }
  .alt-note { font-size: 10px; color: #888; margin: 4px 0 8px; }
  .alt-head { margin-top: 22px; font-size: 13px; color: #9F902A; border-bottom: 1px solid #e5e0c0; padding-bottom: 3px; }
  .grand { display: flex; justify-content: flex-end; gap: 16px; margin-top: 14px; padding-top: 8px; border-top: 3px solid #9F902A; font-size: 14px; font-weight: 700; }
  .clauses { margin-top: 20px; page-break-before: auto; }
  .clause-block { margin-bottom: 10px; page-break-inside: avoid; }
  .clause-block h3 { font-size: 12px; margin: 8px 0 4px; color: #9F902A; border-bottom: 1px solid #e5e0c0; padding-bottom: 2px; }
  .clause-block ul { margin: 0; padding-left: 18px; font-size: 11px; line-height: 1.5; }
  .summ td.num { text-align: right; }
  .summ tr.total td { font-weight: 700; border-top: 2px solid #9F902A; }
  @media print { .no-print { display: none; } }
</style></head>
<body>
  <div class="no-print"><button onclick="window.print()">Print / Save as PDF</button></div>
  <div class="doc">
    <div class="head">
      <div>
        <div class="brand">Architecture BRIO</div>
        <h1>Bill of Quantities — ${esc(projectName || '')}</h1>
      </div>
      <div class="meta">
        Project: ${esc(projectCode || '')}<br/>
        Basis: ${esc(basisLabel)}<br/>
        Currency: ${esc(currency)}<br/>
        Date: ${esc(today)}
      </div>
    </div>
    <table>
      <thead><tr>
        <th>Code</th><th>Description</th><th>Unit</th>
        <th class="num">Quantity</th><th class="num">Rate</th><th class="num">Amount</th>
      </tr></thead>
      <tbody>${sections || '<tr><td colspan="6" style="padding:16px;color:#888">No lines in this bill.</td></tr>'}</tbody>
    </table>
    <div class="grand"><span>Grand total (${esc(basisLabel)})</span><span>${esc(formatMoney(grand, currency))}</span></div>
    ${altSectionsHtml ? `
    <h2 class="alt-head">Alternates considered</h2>
    <div class="alt-note">For each option below the selected line is shown alongside the alternates that were priced but not adopted. Amounts shown are excluded from the grand total above.</div>
    <table>
      <thead><tr>
        <th>Code</th><th>Description</th><th>Unit</th>
        <th class="num">Quantity</th><th class="num">Rate</th><th class="num">Amount</th>
      </tr></thead>
      <tbody>${altSectionsHtml}</tbody>
    </table>` : ''}
    ${clausesHtml(clauses)}
  </div>
  <script>window.addEventListener('load', function () { setTimeout(function () { try { window.print(); } catch (e) {} }, 250); });</script>
</body></html>`;
}

// Which summary rows an export detail level carries. 'headers' keeps the
// top-level rows only — the gold section headers of the trade view, which carry
// their own subtotals, so both levels foot to the same grand total. 'details'
// adds the white sub-trade / alternate rows beneath them. The third tier
// (individual line items, flagged isLine) is never exported at either level.
// Zone and responsibility views are single-level, so the two levels agree there.
export function summaryRowsForDetail(groups = [], detailLevel = 'details') {
  return groups.filter((g) => !g.isLine && (detailLevel !== 'headers' || (g.depth || 0) === 0));
}

// Cost summary document: roll-up by trade or zone, rate per selected area(s),
// + clauses. Rate values are precomputed by the caller (groups[].rates and
// grandRates) so trade (project area) and zone (per-zone area, weighted-avg
// grand) are both handled. rateHeaders: column titles; grandRateNote: e.g.
// 'weighted avg'.
//
// `detailLevel` only labels the document — the caller decides which rows to
// pass in ('headers' = the gold section rows alone, 'details' = those plus the
// white sub-rows beneath them). Section rows always carry their own subtotals,
// so either level foots to the same grand total.
export function renderSummaryHtml({ projectName, projectCode, currency = 'INR', groupMode = 'trade', detailLevel = 'details', groups = [], worksLabel = 'Measured works', measuredQuoted = 0, measuredBilled = 0, provisionalQuoted = 0, provisionalBilled = 0, grandQuoted = 0, grandBilled = 0, rateHeaders = [], measuredRates = [], grandRateNote = '', buildUp = null, clauses = [] }) {
  const today = new Date().toISOString().slice(0, 10);
  const groupLabel = groupMode === 'zone' ? 'Zone' : groupMode === 'responsibility' ? 'Responsibility' : 'Trade';
  const detailLabel = detailLevel === 'headers' ? 'Headers only' : 'Headers + details';
  const rateHead = rateHeaders.map((h) => `<th class="num">${esc(h)}</th>`).join('');
  const rateCells = (rates) => (rates || []).map((v) => `<td class="num">${esc(formatMoney(v, currency))}</td>`).join('');
  const emptyRateCells = rateHeaders.map(() => '<td></td>').join('');
  const colspan = 4 + rateHeaders.length;

  const rowStyle = (g) => {
    if (g.isSection) return ' style="font-weight:700;background:#f7f1d4"';
    if (g.isAlternate) return ' style="color:#888;font-style:italic;background:#fafafa"';
    return '';
  };
  const altRateCells = (g) => (g.isAlternate ? rateHeaders.map(() => '<td></td>').join('') : rateCells(g.rates));
  const pctCell = (g) => (g.isAlternate ? '—' : (measuredQuoted ? ((g.quoted / measuredQuoted) * 100).toFixed(1) + '%' : '—'));
  const rows = groups.map((g) => `<tr${rowStyle(g)}>
    <td style="padding-left:${6 + (g.depth || 0) * 16}px">${esc(g.label)}${g.note ? `<div style="font-size:9px;color:#888;font-weight:400;margin-top:2px">${esc(g.note)}</div>` : ''}</td>
    <td class="num">${esc(formatMoney(g.quoted, currency))}</td>
    <td class="num">${pctCell(g)}</td>
    <td class="num">${esc(formatMoney(g.billed, currency))}</td>
    ${altRateCells(g)}
  </tr>`).join('');

  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8" /><title>Cost summary ${esc(projectCode || '')}</title>
<style>
  @page { size: A4; margin: 16mm 14mm; }
  body { font-family: 'Raleway', -apple-system, system-ui, sans-serif; color: #222; margin: 0; }
  .no-print { margin: 12px; }
  .no-print button { font: inherit; padding: 8px 16px; border: 1px solid #9F902A; background: #9F902A; color: #fff; border-radius: 4px; cursor: pointer; }
  .head { display: flex; justify-content: space-between; border-bottom: 3px solid #9F902A; padding-bottom: 8px; margin-bottom: 14px; }
  .brand { font-weight: 700; font-size: 18px; color: #9F902A; }
  h1 { font-size: 16px; margin: 4px 0 0; }
  .meta { text-align: right; font-size: 11px; color: #555; line-height: 1.6; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  th { background: #9F902A; color: #fff; text-align: left; padding: 6px; }
  th.num, td.num { text-align: right; }
  td { padding: 5px 6px; border-bottom: 1px solid #eee; }
  tr.total td { font-weight: 700; border-top: 2px solid #9F902A; }
  .clauses { margin-top: 20px; }
  .clause-block { margin-bottom: 10px; page-break-inside: avoid; }
  .clause-block h3 { font-size: 12px; margin: 8px 0 4px; color: #9F902A; border-bottom: 1px solid #e5e0c0; padding-bottom: 2px; }
  .clause-block ul { margin: 0; padding-left: 18px; font-size: 11px; line-height: 1.5; }
  .note { font-size: 10px; color: #888; margin-top: 4px; }
  @media print { .no-print { display: none; } }
</style></head>
<body>
  <div class="no-print"><button onclick="window.print()">Print / Save as PDF</button></div>
  <div class="head">
    <div><div class="brand">Architecture BRIO</div><h1>Cost Summary — ${esc(projectName || '')}</h1></div>
    <div class="meta">Project: ${esc(projectCode || '')}<br/>Grouped by: ${esc(groupLabel)}<br/>Detail: ${esc(detailLabel)}<br/>Currency: ${esc(currency)}<br/>Date: ${esc(today)}</div>
  </div>
  <table>
    <thead><tr><th>${esc(groupLabel)}</th><th class="num">Estimate</th><th class="num">%</th><th class="num">Tender</th>${rateHead}</tr></thead>
    <tbody>${rows || `<tr><td colspan="${colspan}" style="padding:16px;color:#888">No priced lines.</td></tr>`}</tbody>
    <tfoot>
      <tr class="total"><td>${esc(worksLabel)}${grandRateNote ? ` (${esc(grandRateNote)})` : ''}</td><td class="num">${esc(formatMoney(measuredQuoted, currency))}</td><td></td><td class="num">${esc(formatMoney(measuredBilled, currency))}</td>${rateCells(measuredRates)}</tr>
      ${(provisionalQuoted || provisionalBilled) ? `<tr><td>Provisional sums &amp; PC items</td><td class="num">${esc(formatMoney(provisionalQuoted, currency))}</td><td></td><td class="num">${esc(formatMoney(provisionalBilled, currency))}</td>${emptyRateCells}</tr>` : ''}
      <tr class="total"><td>Grand total</td><td class="num">${esc(formatMoney(grandQuoted, currency))}</td><td></td><td class="num">${esc(formatMoney(grandBilled, currency))}</td>${emptyRateCells}</tr>
    </tfoot>
  </table>
  ${rateHeaders.length ? '<div class="note">Rate-per-area columns use the estimate amount.</div>' : ''}
  ${buildUp ? `
  <h2 style="font-size:13px;margin:18px 0 6px;color:#9F902A">Bill build-up</h2>
  <table>
    <thead><tr><th>Item</th><th class="num">Estimate</th><th class="num">Tender</th></tr></thead>
    <tbody>
      <tr><td>Net works</td><td class="num">${esc(formatMoney(grandQuoted, currency))}</td><td class="num">${esc(formatMoney(grandBilled, currency))}</td></tr>
      ${buildUp.rows.map((r) => `<tr><td>${esc(r.label)}${r.valueType === 'percent' ? ` (${r.sign === 'deduct' ? '−' : ''}${esc(r.value)}%)` : ''}</td><td class="num">${esc(formatMoney(r.runningQuoted, currency))}</td><td class="num">${esc(formatMoney(r.runningBilled, currency))}</td></tr>`).join('')}
      <tr class="total"><td>Grand total</td><td class="num">${esc(formatMoney(buildUp.grandQuoted, currency))}</td><td class="num">${esc(formatMoney(buildUp.grandBilled, currency))}</td></tr>
    </tbody>
  </table>` : ''}
  ${clausesHtml(clauses)}
  <script>window.addEventListener('load', function () { setTimeout(function () { try { window.print(); } catch (e) {} }, 250); });</script>
</body></html>`;
}

// Open the bill in a new window and trigger the print/save-as-PDF dialog.
export function openBillForPrint(html) {
  const w = window.open('', '_blank');
  if (!w) return false; // popup blocked
  w.document.open();
  w.document.write(html);
  w.document.close();
  return true;
}
